import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, shareReplay } from 'rxjs';
import {
	Solicitacao,
	SolicitacaoComentario,
	SolicitacaoStatus,
	SolicitacoesMe,
} from './solicitacoes.types';

/**
 * API das solicitações dos clientes (backend: /customer-request). A conversa de
 * cada solicitação usa a tabela de comentários (entity customerRequest).
 */
@Injectable({ providedIn: 'root' })
export class SolicitacoesService {
	private readonly _api = environment.url + 'customer-request';
	private _me$: Observable<SolicitacoesMe> | null = null;

	constructor(private _http: HttpClient) { }

	/** Quem está logado e se é admin Automining (guardado na sessão da página). */
	me(): Observable<SolicitacoesMe> {
		this._me$ ??= this._http.get<SolicitacoesMe>(`${this._api}/me`).pipe(shareReplay(1));
		return this._me$;
	}

	/** Todos os sites ativos (o campo Site não depende dos grupos de quem abre). */
	sites(): Observable<string[]> {
		return this._http.get<string[]>(`${this._api}/sites`);
	}

	list(filtro: { status?: SolicitacaoStatus; mine?: boolean } = {}): Observable<Solicitacao[]> {
		let params = new HttpParams();
		if (filtro.status) params = params.set('status', filtro.status);
		if (filtro.mine) params = params.set('mine', 'true');
		return this._http.get<Solicitacao[]>(this._api, { params });
	}

	get(id: string): Observable<Solicitacao> {
		return this._http.get<Solicitacao>(`${this._api}/${id}`);
	}

	create(dados: Record<string, string | null>, arquivos: File[]): Observable<Solicitacao> {
		const form = new FormData();
		Object.entries(dados).forEach(([k, v]) => v != null && v !== '' && form.append(k, v));
		arquivos.forEach((f) => form.append('files', f, f.name));
		return this._http.post<Solicitacao>(this._api, form);
	}

	update(id: string, dados: Partial<Pick<Solicitacao, 'title' | 'description' | 'category' | 'priority' | 'area' | 'siteName'>>): Observable<Solicitacao> {
		return this._http.patch<Solicitacao>(`${this._api}/${id}`, dados);
	}

	changeStatus(
		id: string,
		body: { status: SolicitacaoStatus; note?: string | null; estimatedDate?: string | null; releaseVersion?: string | null }
	): Observable<Solicitacao> {
		return this._http.post<Solicitacao>(`${this._api}/${id}/status`, body);
	}

	plan(id: string, body: { estimatedDate?: string | null; queuePosition?: number; adminResponse?: string | null }): Observable<Solicitacao> {
		return this._http.patch<Solicitacao>(`${this._api}/${id}/planning`, body);
	}

	addAttachments(id: string, arquivos: File[]): Observable<Solicitacao> {
		const form = new FormData();
		arquivos.forEach((f) => form.append('files', f, f.name));
		return this._http.post<Solicitacao>(`${this._api}/${id}/attachments`, form);
	}

	/** Exclui a solicitação (só admin: automining_admin ou system_admin). */
	remove(id: string): Observable<{ id: string; deleted: boolean }> {
		return this._http.delete<{ id: string; deleted: boolean }>(`${this._api}/${id}`);
	}

	removeAttachment(attachmentId: string): Observable<Solicitacao> {
		return this._http.delete<Solicitacao>(`${this._api}/attachments/${attachmentId}`);
	}

	/** O download exige o token, então vem como blob e vira URL local. */
	attachmentBlob(attachmentId: string): Observable<Blob> {
		return this._http.get(`${this._api}/attachments/${attachmentId}`, { responseType: 'blob' });
	}

	comments(id: string): Observable<SolicitacaoComentario[]> {
		return this._http.get<SolicitacaoComentario[]>(`${environment.url}comment/customerRequest/${id}`);
	}

	addComment(id: string, comment: string): Observable<SolicitacaoComentario> {
		return this._http.post<SolicitacaoComentario>(`${environment.url}comment`, {
			comment,
			entity: 'customerRequest',
			entityId: id,
		});
	}
}
